import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@kcftech/design-system';
import { AxesEnabledDisplay } from 'components/node_settings/AxesEnabledDisplay';
import { AxesEnabledValue } from 'components/node_settings/V4EditSettingsModal/CollectionSettingsTab/styled';
import {
    getDisabledNumberOfSamplesOptions,
    getDisabledSamplingFrequencyOptions,
} from 'components/node_settings/V4EditSettingsModal/CollectionSettingsTab/collectionDurationValidation';
import { LowFrequencyCollectionSetting } from 'components/node_settings/V4EditSettingsModal/CollectionSettingsTab/LowFrequencyCollectionSetting';
import { UpdateIntervalSetting } from 'components/node_settings/V4EditSettingsModal/CollectionSettingsTab/UpdateIntervalSetting';
import { NumericOptionDropdown } from 'components/dropdown/NumericOptionDropdown';
import {
    DisabledHint,
    SectionDescription,
} from 'components/node_settings/V4EditSettingsModal/styled';
import { Setting } from 'components/node_settings/V4Settings/Setting';
import { SettingsList } from 'components/node_settings/V4Settings/styled';
import { V4SensorSettings } from 'custom_hooks/data_hooks/v4_sensor_settings/shared';
import { useFeatureFlag } from 'configcat-react';
import { ReactNode } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { getEnabledAxes, OptionChild } from 'utils/node';

type CollectionSettingsProps = Readonly<{
    description: ReactNode;
    samplingFrequencyOptions: OptionChild<number>[];
    numberOfSamplesOptions: OptionChild<number>[];
    updateIntervalOptions: OptionChild<number>[];
    onEditAxes?: () => void;
}>;

export function CollectionSettings({
    description,
    samplingFrequencyOptions,
    numberOfSamplesOptions,
    updateIntervalOptions,
    onEditAxes,
}: CollectionSettingsProps) {
    const { control } = useFormContext<V4SensorSettings>();
    const [
        vibrationX,
        vibrationY,
        vibrationZ,
        samplingFrequencyHz,
        numberOfSamples,
        updateInterval,
    ] = useWatch({
        control,
        name: [
            'vibrationX',
            'vibrationY',
            'vibrationZ',
            'samplingFrequencyHz',
            'numberOfSamples',
            'updateInterval',
        ],
    });

    // Disable the options the API would reject on save (collection duration >= update interval).
    const samplingFrequencyDisabledOptions = getDisabledSamplingFrequencyOptions(
        samplingFrequencyOptions,
        { numberOfSamples, updateInterval },
    );
    const numberOfSamplesDisabledOptions = getDisabledNumberOfSamplesOptions(
        numberOfSamplesOptions,
        { samplingFrequencyHz, updateInterval },
    );

    const { value: lowFrequencyCollectionEnabled } = useFeatureFlag(
        'v4SensorsLowFrequencyCollection',
        false,
    );
    const { value: zAxisEnabled } = useFeatureFlag('v4SensorsZAxis', false);

    // Every axis off makes this a temperature-only sensor, with no vibration to collect.
    const allAxesDisabled = !vibrationX && !vibrationY && !vibrationZ;

    return (
        <>
            <SectionDescription>{description}</SectionDescription>
            <SettingsList>
                <Setting name="Axes Enabled">
                    <AxesEnabledValue>
                        <AxesEnabledDisplay
                            enabledAxes={getEnabledAxes({
                                x: vibrationX,
                                y: vibrationY,
                                z: zAxisEnabled && vibrationZ,
                            })}
                        />
                        {onEditAxes && (
                            <Button
                                appearance="link"
                                spacing="compact"
                                ariaLabel="Edit axes"
                                onClick={onEditAxes}
                            >
                                Edit <FontAwesomeIcon icon={faArrowRight} />
                            </Button>
                        )}
                    </AxesEnabledValue>
                </Setting>

                <Setting name="Sampling Frequency">
                    {allAxesDisabled ? (
                        <DisabledHint>
                            Enable vibration data for axes in Advanced Settings to sample
                            data
                        </DisabledHint>
                    ) : (
                        <Controller
                            control={control}
                            name="samplingFrequencyHz"
                            render={({ field }) => (
                                <NumericOptionDropdown
                                    value={field.value}
                                    options={samplingFrequencyOptions}
                                    onChange={field.onChange}
                                    disabledOptions={samplingFrequencyDisabledOptions}
                                />
                            )}
                        />
                    )}
                </Setting>

                <Setting name="Number of Samples">
                    {allAxesDisabled ? (
                        <DisabledHint>
                            Enable vibration data for axes in Advanced Settings to sample
                            data
                        </DisabledHint>
                    ) : (
                        <Controller
                            control={control}
                            name="numberOfSamples"
                            render={({ field }) => (
                                <NumericOptionDropdown
                                    value={field.value}
                                    options={numberOfSamplesOptions}
                                    onChange={field.onChange}
                                    width="16rem"
                                    disabledOptions={numberOfSamplesDisabledOptions}
                                />
                            )}
                        />
                    )}
                </Setting>

                {lowFrequencyCollectionEnabled && <LowFrequencyCollectionSetting />}

                <UpdateIntervalSetting
                    updateIntervalOptions={updateIntervalOptions}
                    allAxesDisabled={allAxesDisabled}
                />
            </SettingsList>
        </>
    );
}
